/**
 * Terms of Service for Jemmi
 */

export function TermsPage() {
  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="mx-auto max-w-2xl bg-white rounded-xl shadow-sm p-8 lg:p-12">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Terms of Service</h1>
        <p className="text-slate-600 mb-8">Last updated: May 2026</p>

        <div className="prose prose-slate max-w-none space-y-6">
          <section>
            <h2 className="text-2xl font-bold text-slate-900">1. Agreement to Terms</h2>
            <p className="text-slate-700">
              By accessing and using Jemmi (the "Service"), you agree to be bound by these Terms of
              Service. If you do not agree to abide by the above, please do not use this service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900">2. Use License</h2>
            <p className="text-slate-700">
              Permission is granted to temporarily download one copy of the materials (information
              or software) on Jemmi for personal, non-commercial transitory viewing only. This is
              the grant of a license, not a transfer of title, and under this license you may not:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mt-3">
              <li>Modifying or copying the materials</li>
              <li>Using the materials for any commercial purpose or for any public display</li>
              <li>Attempting to decompile or reverse engineer any software on Jemmi</li>
              <li>Removing any copyright or other proprietary notations from the materials</li>
              <li>Transferring the materials to another person or "mirroring" the materials</li>
              <li>Using the materials for any illegal purpose or in violation of applicable laws</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900">3. Content Guidelines & Restrictions</h2>
            <p className="text-slate-700">
              Jemmi is committed to maintaining a safe and respectful community for all users. You
              agree that all content (challenges, submissions, captions, comments) you create or
              submit must comply with the following guidelines:
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-3 space-y-3">
              <div>
                <h4 className="font-semibold text-amber-900">Prohibited Content</h4>
                <p className="text-sm text-amber-800 mt-2">
                  You may not submit or create content that:
                </p>
                <ul className="list-disc list-inside text-sm text-amber-800 space-y-1 mt-2">
                  <li>Contains sexually explicit material, nudity, or erotic content</li>
                  <li>Depicts violence, gore, or graphic harm to people or animals</li>
                  <li>Promotes hate speech, discrimination, or harassment based on protected characteristics</li>
                  <li>Bullies, harasses, or threatens any individual or group</li>
                  <li>Contains illegal content or facilitates illegal activities</li>
                  <li>Infringes intellectual property rights (copyright, trademark, etc.)</li>
                  <li>Impersonates another person or misrepresents identity</li>
                  <li>Spreads misinformation or dangerous medical/health advice</li>
                  <li>Contains spam, advertising, or promotional content</li>
                  <li>Compromises user privacy or security</li>
                </ul>
              </div>
            </div>
            <p className="text-slate-700 mt-4">
              Content will be reviewed using automated systems and human moderators. We reserve the
              right to remove violations and suspend or terminate accounts that repeatedly violate
              these guidelines. Challenge creators are responsible for approving or removing flagged
              submissions from their challenges.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900">4. User Accounts</h2>
            <p className="text-slate-700">
              When you create an account on Jemmi, you agree to:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 mt-3">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the confidentiality of your password and account</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of unauthorized access</li>
              <li>Use your account only for lawful purposes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900">5. Intellectual Property Rights</h2>
            <p className="text-slate-700">
              By submitting content to Jemmi (photos, videos, text), you grant us a worldwide,
              non-exclusive, royalty-free license to use your content for displaying on the Service
              and improving our systems. You retain all ownership rights to your content.
            </p>
            <p className="text-slate-700 mt-3">
              You warrant that any content you submit does not violate third-party intellectual
              property rights.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900">6. Limitation of Liability</h2>
            <p className="text-slate-700">
              In no event shall Jemmi, nor any of its officers, directors and employees, be held
              liable for anything arising out of or in any way connected with your use of this
              Service whether such liability is under contract, tort or otherwise, and including
              but not limited to damages for loss of profits, loss of data or other intangible
              losses (even if Jemmi has been advised of the possibility of such damages).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900">7. Termination</h2>
            <p className="text-slate-700">
              We may terminate or suspend your account and access to the Service immediately,
              without prior notice or liability, for any reason, including if you breach these
              Terms or our Content Guidelines.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900">8. Modifications</h2>
            <p className="text-slate-700">
              Jemmi may revise these Terms at any time without notice. By using this Service after
              changes are posted, you agree to abide by the modified Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900">9. Governing Law</h2>
            <p className="text-slate-700">
              These Terms and Conditions are governed by and construed in accordance with the laws
              of the United States, and you irrevocably submit to the exclusive jurisdiction of
              the courts in that location.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900">10. Contact Us</h2>
            <p className="text-slate-700">
              If you have questions about these Terms, please contact us at{' '}
              <a href="mailto:support@jemmi.fun" className="text-brand-600 hover:underline">
                support@jemmi.fun
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
